import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchAuditLogs, setParams } from 'store/auditLogsSlice';
import { RootState } from 'store/index';
import { commonGridStyles } from 'styles/gridStyles';
import CustomPagination from 'components/common/pagination/pagination';
import { ROWS_PER_PAGE_OPTIONS } from 'constants/pagination';
import { formatDate } from 'utils/date';
import dayjs, { Dayjs } from 'dayjs';
import RangeDatePicker from 'components/common/RangeDatePicker/RangeDatePicker';
import AuditLogDetailModal from './AuditLogDetailModal';
import styles from './AuditLogs.module.scss';
import commonStyles from 'styles/common.module.scss';

const METHOD_OPTIONS = ['post', 'put', 'patch', 'delete'];
const SERVICE_OPTIONS = ['client', 'webhook'];
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i + 1);

const AuditLogs: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { items, total, isLoading, params } = useAppSelector((state: RootState) => state.auditLogs);
  const theme = useAppSelector((state: RootState) => state.ui.theme);

  const [detailKey, setDetailKey] = React.useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchAuditLogs(params));
  }, [dispatch, params]);

  const handleParamChange = useCallback(
    (newParams: any) => {
      dispatch(setParams(newParams));
    },
    [dispatch],
  );

  const handleFromChange = (date: Dayjs | null) => {
    if (date) {
      const to = date.add(24, 'hour');
      handleParamChange({
        from: date.toISOString(),
        to: to.toISOString(),
        page: 1,
      });
    }
  };

  const handleToChange = (date: Dayjs | null) => {
    if (date) {
      const from = date.subtract(24, 'hour');
      handleParamChange({
        from: from.toISOString(),
        to: date.toISOString(),
        page: 1,
      });
    }
  };

  const handlePageChange = (page: number) => {
    handleParamChange({ page });
  };

  const handlePageSizeChange = (limit: number) => {
    handleParamChange({ limit, page: 1 });
  };

  const columns: GridColDef[] = [
    {
      field: 'timestamp',
      headerName: 'Date',
      width: 200,
      renderCell: (params) => (
        <Typography className={styles.timestamp}>{formatDate(params.value)}</Typography>
      ),
    },
    {
      field: 'method',
      headerName: 'Method',
      width: 100,
      renderCell: (params) => (
        <span className={`${styles.method} ${styles[params.value.toLowerCase()]}`}>
          {params.value}
        </span>
      ),
    },
    {
      field: 'path',
      headerName: 'Path',
      width: 300,
      renderCell: (params) => <Typography className={styles.path}>{params.value}</Typography>,
    },
    {
      field: 'userId',
      headerName: 'User ID',
      width: 100,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const userId = params.value;
        if (!userId)
          return (
            <Typography sx={{ fontSize: '13px', color: 'var(--text-secondary)' }}>-</Typography>
          );

        return (
          <Typography
            sx={{
              fontSize: '13px',
              color: 'var(--accent-blue)',
              cursor: 'pointer',
              fontWeight: 600,
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/clients/${userId}`);
            }}
          >
            {userId}
          </Typography>
        );
      },
    },
    {
      field: 'sizeBytes',
      headerName: 'Size (Bytes)',
      width: 130,
      valueFormatter: (value: number) => (value ? `${value.toLocaleString()} B` : '0 B'),
    },

    {
      field: 'actions',
      headerName: 'Action',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="contained"
          sx={{
            bgcolor: 'var(--accent-blue)',
            color: 'white',
            textTransform: 'none',
            fontSize: '12px',
            padding: '4px 12px',
            '&:hover': { bgcolor: 'var(--accent-blue-hover)' },
          }}
          onClick={() => setDetailKey(params.row.key)}
        >
          Details
        </Button>
      ),
    },
  ];

  const renderPagination = () => (
    <CustomPagination
      count={total}
      page={params.page - 1}
      onPageChange={(_, newPage) => handlePageChange(newPage + 1)}
      rowsPerPage={params.limit}
      onRowsPerPageChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
    />
  );

  return (
    <Box className={styles.logsContainer}>
      <Box className={styles.header}>
        <Typography variant="h4" className={styles.title}>
          Audit Logs
        </Typography>
        <Box className={styles.controls}>
          <Box className={styles.filterItem}>
            <RangeDatePicker
              fromDate={params.from ? dayjs(params.from) : null}
              toDate={params.to ? dayjs(params.to) : null}
              onFromChange={handleFromChange}
              onToChange={handleToChange}
              disableConstraints={true}
            />
          </Box>

          <Box className={styles.filterItem} sx={{ minWidth: '100px' }}>
            <FormControl size="small" className={styles.selectContainer}>
              <InputLabel sx={{ color: '#8698a6' }}>Hours</InputLabel>
              <Select
                value={params.hours || ''}
                label="Hours"
                onChange={(e) => {
                  const val = e.target.value;
                  handleParamChange({
                    hours: (val as any) === '' ? undefined : Number(val),
                    page: 1,
                  });
                }}
              >
                <MenuItem value="">None</MenuItem>
                {HOUR_OPTIONS.map((h) => (
                  <MenuItem key={h} value={h}>
                    Last {h} hr
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box className={styles.filterItem}>
            <FormControl size="small" className={styles.selectContainer}>
              <InputLabel sx={{ color: '#8698a6' }}>Service</InputLabel>
              <Select
                value={params.service || 'all'}
                label="Service"
                onChange={(e) =>
                  handleParamChange({
                    service: e.target.value === 'all' ? undefined : e.target.value,
                    page: 1,
                  })
                }
              >
                <MenuItem value="all">All Services</MenuItem>
                {SERVICE_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box className={styles.filterItem}>
            <FormControl size="small" className={styles.selectContainer}>
              <InputLabel sx={{ color: '#8698a6' }}>Method</InputLabel>
              <Select
                value={params.method || 'all'}
                label="Method"
                onChange={(e) =>
                  handleParamChange({
                    method: e.target.value === 'all' ? undefined : e.target.value,
                    page: 1,
                  })
                }
              >
                <MenuItem value="all">All Methods</MenuItem>
                {METHOD_OPTIONS.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m.toUpperCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box className={styles.filterItem}>
            <TextField
              size="small"
              label="User ID"
              placeholder="Search ID"
              value={params.userId || ''}
              onChange={(e) => handleParamChange({ userId: e.target.value || undefined, page: 1 })}
              sx={{
                bgcolor: 'var(--bg-input)',
                borderRadius: '8px',
                '& .MuiInputBase-root': { color: 'var(--text-primary)' },
                '& .MuiInputLabel-root': { color: 'var(--text-secondary)' },
              }}
            />
          </Box>
        </Box>
      </Box>

      <Box className={commonStyles.gridWrapper}>
        {renderPagination()}
        <DataGrid
          rows={items}
          columns={columns}
          getRowId={(row) => row.key}
          loading={isLoading}
          sx={commonGridStyles}
        />
        {renderPagination()}
      </Box>

      <AuditLogDetailModal
        open={!!detailKey}
        logKey={detailKey || ''}
        onClose={() => setDetailKey(null)}
        theme={theme as any}
      />
    </Box>
  );
};

export default AuditLogs;
